<template>
  <div style="background: #f8fafc; min-height: 100vh; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
    <ServicesServiceHero
      title="Development & Feature Work"
      description="Transform your vision with ongoing development support and custom feature implementation"
      icon="💻"
      gradient="development-services"
    />

    <!-- Main Content -->
    <div class="container" style="max-width: 1000px;">
      <div class="row">
        <div class="col-md-12">
          <div class="service-main-card">
            <h2 style="color: #1a202c; font-weight: 700; margin-bottom: 24px; font-size: 2.2rem;">Development & Feature Work</h2>
            <p style="color: #4a5568; line-height: 1.7; font-size: 1.1rem; margin-bottom: 40px;">For new features, UI/UX updates, workflows, integrations, and enhancements. A retainer package keeps us moving forward with continuous improvements while maintaining budget predictability. Pricing scales based on project complexity and codebase size.</p>
            
            <!-- Project Complexity Guide -->
            <div style="background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); padding: 28px; border-radius: 12px; margin-bottom: 40px;">
              <h3 style="color: #1a202c; font-weight: 600; margin-bottom: 20px; font-size: 1.4rem;">📊 Understanding Project Complexity</h3>
              <div class="row">
                <div class="col-md-4 mb-3">
                  <div style="background: white; padding: 18px; border-radius: 10px; height: 100%; border-left: 4px solid #8b5cf6;">
                    <h4 style="color: #8b5cf6; font-weight: 600; margin-bottom: 10px; font-size: 1.1rem;">Simple</h4>
                    <ul style="color: #4a5568; font-size: 0.9rem; margin: 0; padding-left: 20px; line-height: 1.7;">
                      <li>Bug fixes & patches</li>
                      <li>Content updates</li>
                      <li>Minor UI tweaks</li>
                      <li>Simple integrations</li>
                    </ul>
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <div style="background: white; padding: 18px; border-radius: 10px; height: 100%; border-left: 4px solid #8b5cf6;">
                    <h4 style="color: #8b5cf6; font-weight: 600; margin-bottom: 10px; font-size: 1.1rem;">Moderate</h4>
                    <ul style="color: #4a5568; font-size: 0.9rem; margin: 0; padding-left: 20px; line-height: 1.7;">
                      <li>New features</li>
                      <li>API integrations</li>
                      <li>UI/UX improvements</li>
                      <li>Workflow enhancements</li>
                    </ul>
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <div style="background: white; padding: 18px; border-radius: 10px; height: 100%; border-left: 4px solid #8b5cf6;">
                    <h4 style="color: #8b5cf6; font-weight: 600; margin-bottom: 10px; font-size: 1.1rem;">Complex</h4>
                    <ul style="color: #4a5568; font-size: 0.9rem; margin: 0; padding-left: 20px; line-height: 1.7;">
                      <li>Platform modernization</li>
                      <li>Major refactoring</li>
                      <li>Complex integrations</li>
                      <li>Architecture changes</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div style="background: #fef3c7; border-left: 4px solid #f59e0b; padding: 24px; border-radius: 12px; margin-bottom: 40px;">
              <h3 style="color: #92400e; font-weight: 600; margin-bottom: 12px; font-size: 1.3rem;">💡 Why Choose a Retainer?</h3>
              <p style="color: #78350f; line-height: 1.7; margin-bottom: 12px;">A retainer package keeps us moving forward with continuous improvements. We'll suggest updates and you and your team can create a punch list of changes/enhancements. Knock them out a little at a time (stay in retainer budget per month) or do more focused project-based work (retainer plus discounted rate).</p>
            </div>

            <!-- Pay As You Go -->
            <div style="background: #f3f4f6; border-left: 4px solid #6b7280; padding: 24px; border-radius: 12px; margin-bottom: 30px;">
              <h3 style="color: #1a202c; font-weight: 600; margin-bottom: 16px; font-size: 1.6rem;">💳 Pay-As-You-Go — $225/hr</h3>
              <p style="color: #4a5568; line-height: 1.7; margin: 0;">Flexible hourly billing for one-off projects and ad-hoc development work with no commitment required.</p>
            </div>

            <p style="color: #4a5568; font-size: 1.1rem; margin-bottom: 30px; font-style: italic; text-align: center;">Or commit to a retainer tier for significantly reduced hourly rates and priority support:</p>

            <!-- Retainer Tiers -->
            <div class="row">
              <!-- Tier 1: Starter Package -->
              <div class="col-md-12 mb-4">
                <div :style="{ background: 'linear-gradient(135deg, var(--development-services-start) 0%, var(--development-services-end) 100%)', color: 'white', padding: '32px', borderRadius: '16px' }">
                  <div style="display: flex; justify-content: space-between; align-items: start; flex-wrap: wrap; gap: 16px;">
                    <div>
                      <h4 style="font-weight: 700; margin-bottom: 8px; font-size: 1.5rem;">Tier 1: Starter Package</h4>
                      <p style="opacity: 0.9; margin: 0; font-size: 0.95rem;">Ideal for simple projects and ongoing maintenance</p>
                    </div>
                    <div style="background: rgba(255,255,255,0.2); padding: 16px 24px; border-radius: 12px; backdrop-filter: blur(10px);">
                      <div style="font-size: 2rem; font-weight: 800;">$165-$185</div>
                      <div style="opacity: 0.9; font-size: 0.9rem;">per hour</div>
                    </div>
                  </div>
                  <div style="border-top: 1px solid rgba(255,255,255,0.2); margin: 20px 0;"></div>
                  <div style="background: rgba(255,255,255,0.15); padding: 16px; border-radius: 8px; margin-bottom: 20px;">
                    <strong>Monthly Commitment:</strong> 10-15 hours/month minimum<br>
                    <span style="opacity: 0.9; font-size: 0.9rem;">Rate varies based on project complexity</span>
                  </div>
                  <ul style="margin: 0; padding-left: 20px; line-height: 1.9;">
                    <li>Priority scheduling over pay-as-you-go clients</li>
                    <li><strong>Rollover policy:</strong> Unused hours roll over 1 month</li>
                    <li>Email and chat support included</li>
                    <li>Regular progress updates</li>
                    <li>Dedicated project tracking</li>
                  </ul>
                  <p style="margin-top: 16px; opacity: 0.85; font-size: 0.9rem; font-style: italic;">Best for: Bug fixes, minor enhancements, content updates, and incremental improvements.</p>
                </div>
              </div>

              <!-- Tier 2: Growth Package -->
              <div class="col-md-12 mb-4">
                <div style="position: relative;">
                  <div style="position: absolute; top: -12px; right: 20px; background: #10b981; color: white; padding: 6px 16px; border-radius: 20px; font-weight: 600; font-size: 0.85rem; box-shadow: 0 2px 8px rgba(16,185,129,0.3);">MOST POPULAR</div>
                  <div :style="{ background: 'linear-gradient(135deg, var(--development-services-start) 0%, var(--development-services-end) 100%)', color: 'white', padding: '32px', borderRadius: '16px', border: '3px solid #10b981' }">
                    <div style="display: flex; justify-content: space-between; align-items: start; flex-wrap: wrap; gap: 16px;">
                      <div>
                        <h4 style="font-weight: 700; margin-bottom: 8px; font-size: 1.5rem;">Tier 2: Growth Package</h4>
                        <p style="opacity: 0.9; margin: 0; font-size: 0.95rem;">Perfect for moderate complexity and regular feature development</p>
                      </div>
                      <div style="background: rgba(255,255,255,0.2); padding: 16px 24px; border-radius: 12px; backdrop-filter: blur(10px);">
                        <div style="font-size: 2rem; font-weight: 800;">$145-$165</div>
                        <div style="opacity: 0.9; font-size: 0.9rem;">per hour</div>
                      </div>
                    </div>
                    <div style="border-top: 1px solid rgba(255,255,255,0.2); margin: 20px 0;"></div>
                    <div style="background: rgba(255,255,255,0.15); padding: 16px; border-radius: 8px; margin-bottom: 20px;">
                      <strong>Monthly Commitment:</strong> 20-30 hours/month minimum<br>
                      <span style="opacity: 0.9; font-size: 0.9rem;">Rate varies based on project complexity</span>
                    </div>
                    <p style="margin-bottom: 12px; font-weight: 600;">Everything in Tier 1, plus:</p>
                    <ul style="margin: 0; padding-left: 20px; line-height: 1.9;">
                      <li>Higher priority scheduling (ahead of Tier 1)</li>
                      <li><strong>Rollover policy:</strong> Unused hours rollover 2 months</li>
                      <li>Monthly planning and review calls</li>
                      <li>Proactive feature suggestions and roadmap planning</li>
                      <li>Dedicated communication channel</li>
                      <li>Architecture consultation included</li>
                    </ul>
                    <p style="margin-top: 16px; opacity: 0.85; font-size: 0.9rem; font-style: italic;">Best for: New feature development, UI/UX improvements, workflow enhancements, and integration projects.</p>
                  </div>
                </div>
              </div>

              <!-- Tier 3: Premium Package -->
              <div class="col-md-12 mb-4">
                <div :style="{ background: 'linear-gradient(135deg, var(--development-services-start) 0%, var(--development-services-end) 100%)', color: 'white', padding: '32px', borderRadius: '16px' }">
                  <div style="display: flex; justify-content: space-between; align-items: start; flex-wrap: wrap; gap: 16px;">
                    <div>
                      <h4 style="font-weight: 700; margin-bottom: 8px; font-size: 1.5rem;">Tier 3: Premium Package</h4>
                      <p style="opacity: 0.9; margin: 0; font-size: 0.95rem;">Full-scale development partnership for complex projects</p>
                    </div>
                    <div style="background: rgba(255,255,255,0.2); padding: 16px 24px; border-radius: 12px; backdrop-filter: blur(10px);">
                      <div style="font-size: 2rem; font-weight: 800;">$125-$145</div>
                      <div style="opacity: 0.9; font-size: 0.9rem;">per hour</div>
                    </div>
                  </div>
                  <div style="border-top: 1px solid rgba(255,255,255,0.2); margin: 20px 0;"></div>
                  <div style="background: rgba(255,255,255,0.15); padding: 16px; border-radius: 8px; margin-bottom: 20px;">
                    <strong>Monthly Commitment:</strong> 30-50 hours/month minimum<br>
                    <span style="opacity: 0.9; font-size: 0.9rem;">Rate varies based on project complexity and scope</span>
                  </div>
                  <p style="margin-bottom: 12px; font-weight: 600;">Everything in Tier 2, plus:</p>
                  <ul style="margin: 0; padding-left: 20px; line-height: 1.9;">
                    <li>Highest priority scheduling (top of the queue)</li>
                    <li><strong>Rollover policy:</strong> Unused hours rollover 3 months</li>
                    <li>Bi-weekly planning and status update calls</li>
                    <li>Dedicated project management and tracking</li>
                    <li>Strategic technical consulting included</li>
                    <li>Direct access for urgent issues</li>
                    <li>Comprehensive documentation and knowledge transfer</li>
                  </ul>
                  <p style="margin-top: 16px; opacity: 0.85; font-size: 0.9rem; font-style: italic;">Best for: Major feature rollouts, platform modernization, comprehensive UI/UX overhauls, and complex integrations.</p>
                </div>
              </div>
            </div>

            <!-- What's Included -->
            <div style="background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%); padding: 32px; border-radius: 16px; margin-top: 40px; border-left: 4px solid #8b5cf6;">
              <h3 style="color: #1a202c; font-weight: 600; margin-bottom: 20px; font-size: 1.5rem;">📦 What's Included in Development Retainers</h3>
              <div class="row">
                <div class="col-md-6 mb-3">
                  <div style="background: white; padding: 20px; border-radius: 12px; height: 100%;">
                    <h4 style="color: #8b5cf6; font-weight: 600; margin-bottom: 12px; font-size: 1.1rem;">✨ UI/UX Updates</h4>
                    <p style="color: #4a5568; margin: 0; line-height: 1.6;">Interface improvements, responsive design, and user experience enhancements</p>
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <div style="background: white; padding: 20px; border-radius: 12px; height: 100%;">
                    <h4 style="color: #8b5cf6; font-weight: 600; margin-bottom: 12px; font-size: 1.1rem;">🔄 New Workflows</h4>
                    <p style="color: #4a5568; margin: 0; line-height: 1.6;">Process automation, business logic implementation, and workflow optimization</p>
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <div style="background: white; padding: 20px; border-radius: 12px; height: 100%;">
                    <h4 style="color: #8b5cf6; font-weight: 600; margin-bottom: 12px; font-size: 1.1rem;">🔌 Integrations</h4>
                    <p style="color: #4a5568; margin: 0; line-height: 1.6;">Third-party API integrations, data synchronization, and service connections</p>
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <div style="background: white; padding: 20px; border-radius: 12px; height: 100%;">
                    <h4 style="color: #8b5cf6; font-weight: 600; margin-bottom: 12px; font-size: 1.1rem;">🎓 Course Management</h4>
                    <p style="color: #4a5568; margin: 0; line-height: 1.6;">LMS enhancements, content management, and educational platform features</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pricing Examples -->
            <div style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); padding: 32px; border-radius: 16px; margin-top: 40px; border-left: 4px solid #f59e0b;">
              <h3 style="color: #92400e; font-weight: 600; margin-bottom: 20px; font-size: 1.5rem;">💰 Pricing Examples</h3>
              <p style="color: #78350f; margin-bottom: 20px; line-height: 1.7;">Here are sample monthly costs to help you understand how project complexity impacts pricing:</p>
              
              <div class="row">
                <div class="col-md-4 mb-3">
                  <div style="background: white; padding: 20px; border-radius: 10px; height: 100%;">
                    <h4 style="color: #f59e0b; font-weight: 600; margin-bottom: 12px; font-size: 1.1rem;">Simple Projects</h4>
                    <div style="color: #4a5568; font-size: 0.9rem; line-height: 1.7;">
                      <strong>Tier 1 Example:</strong><br>
                      12 hrs × $165/hr = <strong>$1,980/mo</strong><br><br>
                      <strong>Tier 2 Example:</strong><br>
                      22 hrs × $145/hr = <strong>$3,190/mo</strong>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <div style="background: white; padding: 20px; border-radius: 10px; height: 100%;">
                    <h4 style="color: #f59e0b; font-weight: 600; margin-bottom: 12px; font-size: 1.1rem;">Moderate Projects</h4>
                    <div style="color: #4a5568; font-size: 0.9rem; line-height: 1.7;">
                      <strong>Tier 1 Example:</strong><br>
                      15 hrs × $175/hr = <strong>$2,625/mo</strong><br><br>
                      <strong>Tier 2 Example:</strong><br>
                      28 hrs × $155/hr = <strong>$4,340/mo</strong>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <div style="background: white; padding: 20px; border-radius: 10px; height: 100%;">
                    <h4 style="color: #f59e0b; font-weight: 600; margin-bottom: 12px; font-size: 1.1rem;">Complex Projects</h4>
                    <div style="color: #4a5568; font-size: 0.9rem; line-height: 1.7;">
                      <strong>Tier 2 Example:</strong><br>
                      30 hrs × $165/hr = <strong>$4,950/mo</strong><br><br>
                      <strong>Tier 3 Example:</strong><br>
                      40 hrs × $135/hr = <strong>$5,400/mo</strong>
                    </div>
                  </div>
                </div>
              </div>
              <p style="color: #78350f; margin-top: 16px; margin-bottom: 0; font-style: italic; font-size: 0.95rem;">These are examples only. Your actual pricing will be determined after a project assessment.</p>
            </div>

            <!-- Important Notes -->
            <div style="background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); padding: 32px; border-radius: 16px; margin-top: 40px; border-left: 4px solid #3b82f6;">
              <h3 style="color: #1a202c; font-weight: 600; margin-bottom: 20px; font-size: 1.5rem;">📋 Important Information</h3>
              <div style="color: #4a5568; line-height: 1.8;">
                <p style="margin-bottom: 12px;"><strong>Hourly rates are determined by:</strong></p>
                <ul style="margin-bottom: 20px;">
                  <li>Complexity of the features and requirements</li>
                  <li>Current state and architecture of your codebase</li>
                  <li>Integration requirements with existing systems</li>
                  <li>Technology stack and tools involved</li>
                  <li>Timeline and priority level</li>
                </ul>
                <p style="margin-bottom: 12px;"><strong>How retainers work:</strong></p>
                <ul style="margin-bottom: 20px;">
                  <li>Monthly commitment with minimum hours based on tier</li>
                  <li>Rollover periods allow flexibility in project scheduling</li>
                  <li>Significantly reduced hourly rates compared to pay-as-you-go</li>
                  <li>Priority access and dedicated support</li>
                  <li>Cancel or adjust tier with 30 days notice</li>
                </ul>
                <p style="margin: 0; font-style: italic;">For infrastructure and platform maintenance, see our <NuxtLink to="/services/platform-maintenance" style="color: #3b82f6; text-decoration: underline;">Platform Maintenance</NuxtLink> service offerings.</p>
              </div>
            </div>

            <!-- CTA Section -->
            <div :style="{ background: `linear-gradient(135deg, var(--development-services-start) 0%, var(--development-services-end) 100%)`, color: 'white', padding: '40px', borderRadius: '16px', marginTop: '50px', textAlign: 'center' }">
              <h3 style="font-weight: 700; margin-bottom: 20px; font-size: 1.8rem;">Ready to Build Something Amazing?</h3>
              <p style="font-size: 1.1rem; opacity: 0.95; line-height: 1.6; margin-bottom: 30px;">Whether you need a one-time feature or ongoing development support, we're here to turn your vision into reality. Let's discuss your project and find the perfect package for your needs.</p>
              <p style="margin: 0; font-size: 1.05rem; opacity: 0.9;">Contact us to schedule a consultation and get started on your next big feature.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
useHead({
  title: 'Development & Feature Work | Geek Cafe',
  meta: [
    { name: 'description', content: 'Custom development retainer packages for new features, UI/UX updates, workflows, and integrations. Flexible pay-as-you-go or choose a retainer tier.' }
  ]
})
</script>

<style scoped src="@/assets/css/service-detail-card.css"></style>
